import {
  type AccessArea,
  type AddMemberBody,
  MemberListResponseSchema,
  MemberSchema,
  PasswordLinkResponseSchema,
  type Role,
} from '@clinic/contracts';
import { createInfiniteQuery, useQueryClient } from '@tanstack/svelte-query';
import { createMutation } from '../../../shared/api/mutation';
import { apiRequest } from '../../../shared/api/client';
import { meQueryKey } from '../../../shared/auth/auth.svelte';

export const membersKey = (orgId: string) => ['org', orgId, 'members'] as const;

export function useMembers(orgId: string) {
  return createInfiniteQuery(() => ({
    queryKey: membersKey(orgId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      apiRequest(`/v1/orgs/current/members?limit=50${pageParam ? `&cursor=${pageParam}` : ''}`, {
        orgId,
        schema: MemberListResponseSchema,
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor,
  }));
}

function useMemberMutation<V, R>(orgId: string, fn: (vars: V) => Promise<R>) {
  const queryClient = useQueryClient();
  return createMutation(() => ({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: membersKey(orgId) }),
        queryClient.invalidateQueries({ queryKey: ['org', orgId, 'audit'] }),
        // the caller may have changed their own roles
        queryClient.invalidateQueries({ queryKey: meQueryKey }),
      ]),
  }));
}

export function useAddMember(orgId: string) {
  return useMemberMutation(orgId, (body: AddMemberBody) =>
    apiRequest('/v1/orgs/current/members', { method: 'POST', body, orgId, schema: MemberSchema }),
  );
}

export function useSetMemberRoles(orgId: string) {
  return useMemberMutation(orgId, ({ id, roles, displayName }: { id: string; roles: Role[]; displayName?: string }) =>
    apiRequest(`/v1/orgs/current/members/${id}/roles`, {
      method: 'PUT',
      body: { roles, ...(displayName?.trim() ? { displayName } : {}) },
      orgId,
      schema: MemberSchema,
    }),
  );
}

/** The admin's ticks for a member (ADR-042); null puts them back on the roles' defaults. */
export function useSetMemberAccess(orgId: string) {
  return useMemberMutation(orgId, ({ id, areas }: { id: string; areas: AccessArea[] | null }) =>
    apiRequest(`/v1/orgs/current/members/${id}/access`, { method: 'PUT', body: { areas }, orgId, schema: MemberSchema }),
  );
}

/** A one-time link for a member to set or reset their password (ADR-033); the token is shown once. */
export function useIssuePasswordLink(orgId: string) {
  const queryClient = useQueryClient();
  return createMutation(() => ({
    mutationFn: (membershipId: string) =>
      apiRequest(`/v1/orgs/current/members/${membershipId}/password-link`, {
        method: 'POST',
        orgId,
        schema: PasswordLinkResponseSchema,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org', orgId, 'audit'] }),
  }));
}

export function useRemoveMember(orgId: string) {
  return useMemberMutation(orgId, (id: string) =>
    apiRequest(`/v1/orgs/current/members/${id}/remove`, { method: 'POST', orgId, schema: MemberSchema }),
  );
}
